import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import moment from 'moment'
import { dateIsAfterOrSame } from 'lib/datetime/dateUtils'

const CruiseAlertBannerWrapper = styled.div`
  padding-top: ${rem(16)};
`

type AlertSchedule = {
  shipNames?: Array<string>
  rateCodes?: Array<string>
  departureDate?: Date
  startDateUTC: moment.Moment
  endDateUTC: moment.Moment
  cruiseLineCode: string
}

const ALERT_SCHEDULES: Array<AlertSchedule> = [
  {
    departureDate: new Date('2025-03-29'),
    startDateUTC: moment.utc('2024-11-20 00:00:00'), // 2024-11-20 11:00:00 Sydney time
    endDateUTC: moment.utc('2025-05-01 12:59:59'), // 2025-05-01 23:59:59 Sydney time
    cruiseLineCode: 'POA',
  },
  {
    startDateUTC: moment.utc('2025-01-30 04:00:00'), // 2025-01-29 23:00:00 ET
    endDateUTC: moment.utc('2025-01-31 04:00:00'), // 2025-01-30 23:00:00 ET
    cruiseLineCode: 'DCL',
  },
]

interface Props {
  offer: App.CruiseOffer
}

function CruiseAlertBanner({ offer }: Props) {
  const offerRates = useMemo(
    () => Object.values(offer.lowestDeparturePricesByCategory)
      .flatMap((categories) => Object.values(categories).map(({ rateCode }) => rateCode)),
    [offer],
  )

  const {
    isAlertActive,
    hasShipInAlert,
    hasRateCodesInAlert,
    hasDepartureDateInAlert,
  } = useMemo(() => {
    const activeSchedules = ALERT_SCHEDULES.filter((rule) => (
      moment.utc().isBetween(rule.startDateUTC, rule.endDateUTC) &&
      rule.cruiseLineCode === offer.cruiseLine.code
    ))

    const hasShipInAlert = activeSchedules.some(({ shipNames }) => shipNames?.includes(offer.ship.name))
    const hasRateCodesInAlert = activeSchedules.some(({ rateCodes }) => rateCodes?.some((rateCode) => offerRates.includes(rateCode)))

    const hasDepartureDateInAlert = activeSchedules.some(({ departureDate }) => {
      if (!departureDate) {
        return false
      }

      return offer.departureDates?.some((date) => dateIsAfterOrSame(new Date(date), departureDate))
    })

    const isAlertActive = !!activeSchedules.length

    return {
      isAlertActive,
      hasShipInAlert,
      hasRateCodesInAlert,
      hasDepartureDateInAlert,
    }
  }, [offer.cruiseLine.code, offer.ship.name, offer.departureDates, offerRates])

  const description = useMemo(() => {
    if (hasShipInAlert) {
      return `${offer.ship.name} sailings are currently undergoing scheduled maintenance and will resume soon. We appreciate your patience.`
    }

    if (hasRateCodesInAlert) {
      return 'Some sailings are currently undergoing scheduled maintenance and will resume soon. We appreciate your patience.'
    }

    if (hasDepartureDateInAlert) {
      return 'From 29th of March, 2025, this itinerary and all other P&O itineraries will be operated by Carnival.'
    }

    return 'This cruise line is currently undergoing scheduled maintenance and will resume soon. We appreciate your patience.'
  }, [hasShipInAlert, hasRateCodesInAlert, hasDepartureDateInAlert, offer.ship.name])

  const heading = useMemo(() => {
    if (hasDepartureDateInAlert) {
      return `${offer.cruiseLine.name} - Additional Info`
    }

    return `${offer.cruiseLine.name} - Scheduled Maintenance`
  }, [hasDepartureDateInAlert, offer.cruiseLine.name])

  return <>
    {isAlertActive && <CruiseAlertBannerWrapper>
      <LayoutContainer>
        <MessageBanner
          kind="informational-primary"
          heading={heading}
          description={description}
        />
      </LayoutContainer>
    </CruiseAlertBannerWrapper>}
  </>
}

export default CruiseAlertBanner
